import { loadRawAudioProcessor } from "./rawAudioProcessor.generated.js";
import { isIosDevice } from "./compatibility.js";
import { addLibsamplerateModule } from "./addLibsamplerateModule.js";
import { createAnalyserVolumeProvider, } from "./volumeProvider.js";
const defaultConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    // Automatic gain control helps maintain a steady volume level with microphones: https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings/autoGainControl
    autoGainControl: true,
    // Mono audio for better echo cancellation
    channelCount: { ideal: 1 },
};
export class MediaDeviceInput {
    context;
    analyser;
    worklet;
    inputStream;
    mediaStreamSource;
    permissions;
    onError;
    static async create({ sampleRate, format, preferHeadphonesForIosDevices, inputDeviceId, workletPaths, libsampleratePath, onError, }) {
        let context = null;
        let inputStream = null;
        try {
            const options = {
                sampleRate: { ideal: sampleRate },
                ...defaultConstraints,
            };
            if (isIosDevice() && preferHeadphonesForIosDevices) {
                const availableDevices = await window.navigator.mediaDevices.enumerateDevices();
                const idealDevice = availableDevices.find(d => 
                // cautious to include "bluetooth" in the search
                // as might trigger bluetooth speakers
                d.kind === "audioinput" &&
                    ["airpod", "headphone", "earphone"].find(keyword => d.label.toLowerCase().includes(keyword)));
                if (idealDevice) {
                    options.deviceId = { ideal: idealDevice.deviceId };
                }
            }
            if (inputDeviceId) {
                options.deviceId =
                    MediaDeviceInput.getDeviceIdConstraint(inputDeviceId);
            }
            const supportsSampleRateConstraint = navigator.mediaDevices.getSupportedConstraints().sampleRate;
            context = new window.AudioContext(supportsSampleRateConstraint ? { sampleRate } : {});
            const analyser = context.createAnalyser();
            if (!supportsSampleRateConstraint) {
                await addLibsamplerateModule(context, libsampleratePath);
            }
            await loadRawAudioProcessor(context.audioWorklet, workletPaths?.rawAudioProcessor);
            const constraints = { voiceIsolation: true, ...options };
            inputStream = await navigator.mediaDevices.getUserMedia({
                audio: constraints,
            });
            const source = context.createMediaStreamSource(inputStream);
            const worklet = new AudioWorkletNode(context, "rawAudioProcessor");
            worklet.port.postMessage({ type: "setFormat", format, sampleRate });
            source.connect(analyser);
            analyser.connect(worklet);
            await context.resume();
            const permissions = await navigator.permissions.query({
                name: "microphone",
            });
            return new MediaDeviceInput(context, analyser, worklet, inputStream, source, permissions, onError);
        }
        catch (error) {
            inputStream?.getTracks().forEach(track => {
                track.stop();
            });
            context?.close();
            throw error;
        }
    }
    // Use { ideal } on iOS as a defensive measure - some iOS versions may not support { exact } for deviceId constraints
    static getDeviceIdConstraint(deviceId) {
        if (!deviceId) {
            return undefined;
        }
        return isIosDevice() ? { ideal: deviceId } : { exact: deviceId };
    }
    muted = false;
    volumeProvider;
    constructor(context, analyser, worklet, inputStream, mediaStreamSource, permissions, onError = console.error) {
        this.context = context;
        this.analyser = analyser;
        this.worklet = worklet;
        this.inputStream = inputStream;
        this.mediaStreamSource = mediaStreamSource;
        this.permissions = permissions;
        this.onError = onError;
        this.permissions.addEventListener("change", this.handlePermissionsChange);
        // Start the MessagePort to enable addEventListener to work
        // (required when using addEventListener instead of onmessage)
        this.worklet.port.start();
        this.volumeProvider = createAnalyserVolumeProvider(analyser, context.sampleRate);
    }
    getAnalyser() {
        return this.analyser;
    }
    getVolume() {
        if (this.muted)
            return 0;
        return this.volumeProvider.getVolume();
    }
    getByteFrequencyData(buffer) {
        if (this.muted) {
            buffer.fill(0);
            return;
        }
        this.volumeProvider.getByteFrequencyData(buffer);
    }
    isMuted() {
        return this.muted;
    }
    addListener(listener) {
        this.worklet.port.addEventListener("message", listener);
    }
    removeListener(listener) {
        this.worklet.port.removeEventListener("message", listener);
    }
    forgetInputStreamAndSource() {
        for (const track of this.inputStream.getTracks()) {
            track.stop();
        }
        this.mediaStreamSource.disconnect();
    }
    async close() {
        this.forgetInputStreamAndSource();
        this.permissions.removeEventListener("change", this.handlePermissionsChange);
        await this.context.close();
    }
    async setMuted(isMuted) {
        this.muted = isMuted;
        this.worklet.port.postMessage({ type: "setMuted", isMuted });
    }
    settingInput = false;
    async setDevice(config) {
        try {
            if (this.settingInput) {
                throw new Error("Input device is already being set");
            }
            this.settingInput = true;
            // Extract inputDeviceId from config
            const inputDeviceId = config?.inputDeviceId;
            // Note: sampleRate, format, and preferHeadphonesForIosDevices cannot be
            // changed on an existing input (would require recreating the AudioContext).
            // These options are only used during initial MediaDeviceInput.create()
            // Create new constraints with the specified device or use default
            const options = {
                ...defaultConstraints,
            };
            if (inputDeviceId) {
                options.deviceId =
                    MediaDeviceInput.getDeviceIdConstraint(inputDeviceId);
            }
            // If inputDeviceId is undefined, don't set deviceId constraint - browser uses default
            const constraints = { voiceIsolation: true, ...options };
            // Get new media stream with the specified device before forgetting the old one
            // this prevents unintended interruption of the audio stream in case the new stream isn't obtained
            const newInputStream = await navigator.mediaDevices.getUserMedia({
                audio: constraints,
            });
            this.forgetInputStreamAndSource();
            // Replace the stream and create new source
            this.inputStream = newInputStream;
            this.mediaStreamSource =
                this.context.createMediaStreamSource(newInputStream);
            // Reconnect the audio graph
            this.mediaStreamSource.connect(this.analyser);
        }
        catch (error) {
            this.onError("Failed to switch input device:", error);
            throw error;
        }
        finally {
            this.settingInput = false;
        }
    }
    handlePermissionsChange = () => {
        if (this.permissions.state === "denied") {
            this.onError("Microphone permission denied");
            // TODO: Tell the user to grant permission in some other way
        }
        else if (!this.settingInput) {
            // Let's try to reset the input device, but only if we're not already in the process of setting it
            const [track] = this.inputStream.getAudioTracks();
            const { deviceId } = track?.getSettings() ?? {};
            this.setDevice({ inputDeviceId: deviceId }).catch(error => {
                this.onError("Failed to reset input device after permission change:", error);
            });
        }
    };
}
//# sourceMappingURL=input.js.map